import React from 'react';
import PropTypes from 'prop-types';
import { Box, Paper, Typography } from '@mui/material';
import * as C from '../../../theme/colors.constants';

/**
 * 5 cards de resumo da aba Recebimentos (AW2-1133 Etapa 2) — dado real de
 * `infoAdicionaisAcordo` (`ControleProcessosPage.jsx`, já buscado via
 * `getDadosResumo(processo.id)`, mesmo endpoint que `Acordos.jsx` real usa
 * para `InfoAdicionaisAcordo.jsx`/`processoComTitulosLiberados` — nenhum
 * fetch novo, o dado só estava subutilizado).
 *
 * Nome IGUAL ao componente de produção equivalente
 * (`src/pages/InspecionarProcesso/Acordos/InfoAdicionaisAcordo.jsx`) —
 * deliberado, não descuido: segue a convenção já estabelecida no módulo de
 * manter o nome do componente real ao portar ("Port de X (tela real)"),
 * para rastreabilidade entre produção e protótipo. Caminhos de import são
 * distintos (sem colisão real), e a busca por "InfoAdicionaisAcordo" nos
 * dois domínios é o comportamento desejado, não um problema a evitar.
 */
const CARDS = [
  { campo: 'qtdAcordosQuitados', label: 'Quitados' },
  { campo: 'qtdAcordosAtivos', label: 'Ativos' },
  { campo: 'qtdAcordosQuebrados', label: 'Quebrados' },
  { campo: 'qtdAcordosSimulados', label: 'Simulações' },
  { campo: 'qtdAcordosSimulacoesPendentes', label: 'Propostas' },
];

/**
 * Soma dos 5 cards — fonte única do "Total de acordos" exibido em
 * `AbaRecebimento.jsx` (achado em QA ao vivo, AW2-1133 Etapa 12): o rótulo
 * usava `acordosTotal` (total PAGINADO da grid, `usePaginacaoOrdenacaoPorProcesso`)
 * em vez da soma dos cards — como a grid é filtrada pelo toggle "Exibir
 * apenas Acordos Ativos e Propostas" (default ligado) mas os 5 cards sempre
 * refletem o universo completo (`infoAdicionaisAcordo`, não filtrado por
 * toggle), o rótulo divergia da soma sempre que havia Quitados/Quebrados/
 * Simulações fora do filtro ativo — violação literal do 5º critério de
 * aceite do Jira ("a soma dos 5 cards é exatamente igual ao Total de
 * acordos") e do próprio cenário de borda da issue ("inconsistência entre
 * soma dos cards e total exibido não deve ocorrer"). Iterar `CARDS` (mesmo
 * array table-driven que já alimenta a renderização) em vez de somar os 5
 * campos soltos garante que a soma nunca diverge silenciosamente se um card
 * for adicionado/removido aqui sem atualizar um segundo lugar. `?? 0` por
 * campo, mesma regra já usada na renderização de cada card.
 */
export function somarTotalAcordos(dados) {
  return CARDS.reduce((total, { campo }) => total + (dados?.[campo] ?? 0), 0);
}

export default function InfoAdicionaisAcordo({ dados }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(5, minmax(0, 1fr))' },
        gap: 'clamp(8px, 0.6vw, 16px)',
      }}
    >
      {CARDS.map(({ campo, label }) => (
        <Paper
          key={campo}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.25,
            p: 1.25,
            backgroundColor: C.paleGrey,
            boxShadow: 'none',
            border: `1px solid ${C.borderGrey}`,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>{label}</Typography>
          {/* `?? 0`, não `|| 0` — uma contagem 0 genuína não pode virar "sem dado" */}
          <Typography sx={{ fontWeight: 700, color: C.navy }}>{dados?.[campo] ?? 0}</Typography>
        </Paper>
      ))}
    </Box>
  );
}

InfoAdicionaisAcordo.propTypes = {
  dados: PropTypes.shape({
    qtdAcordosQuitados: PropTypes.number,
    qtdAcordosAtivos: PropTypes.number,
    qtdAcordosQuebrados: PropTypes.number,
    qtdAcordosSimulados: PropTypes.number,
    qtdAcordosSimulacoesPendentes: PropTypes.number,
  }),
};

InfoAdicionaisAcordo.defaultProps = { dados: null };
